import { Box, Button, Typography } from '@mui/material';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public override state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  public override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // We can log the error to an error reporting service here
    // biome-ignore lint/suspicious/noConsole: ErrorBoundary needs to log errors
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
    // Also consider reloading the page or redirecting to home
    window.location.href = '/';
  };

  public override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            textAlign: 'center',
            p: 3,
            bgcolor: 'background.default',
          }}
        >
          <img
            src="/Bidlogiq_Logo-Long_Full colours.png"
            alt="BIDLOGIQ Logo"
            style={{ maxWidth: '300px', marginBottom: '32px' }}
          />
          <Typography variant="h4" color="error" gutterBottom sx={{ fontWeight: 'bold' }}>
            Something went wrong.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2, maxWidth: 600 }}>
            An unexpected error occurred in the application. The error has been logged, but if the
            issue persists, please contact support.
          </Typography>

          <Box
            sx={{
              mb: 4,
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              maxWidth: 600,
              width: '100%',
            }}
          >
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Support Contact
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Email: support@bidlogiq.ai
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Phone: +61 (0)7 2143 6035
            </Typography>
          </Box>

          <Button
            variant="contained"
            onClick={this.handleReset}
            sx={{
              px: 4,
              py: 1,
              bgcolor: (theme) =>
                (theme.palette as unknown as Record<string, Record<string, string>>).bidlogiq
                  ?.brick || 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: (theme) =>
                  (theme.palette as unknown as Record<string, Record<string, string>>).bidlogiq
                    ?.brick || 'primary.dark',
              },
            }}
          >
            Return to Home
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
